import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  CloudUpload as DeployIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon,
  Terminal as TerminalIcon,
  Code as CodeIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';

/**
 * Developer Tools Page
 * Access to development tools, Git, deployment, database, and API testing
 */
const DeveloperToolsPage: React.FC = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Development Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Access your development tools, repositories, and testing utilities
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Git Repository Browser */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GitHubIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h6">Git Repository Browser</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Browse repositories, view branches, commits, and pull requests directly from the dashboard.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="View all repositories" secondary="Access your assigned repos" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Branch management" secondary="Create, merge, delete branches" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Commit history" secondary="View recent commits and changes" />
                </ListItem>
              </List>
            </CardContent>
            <CardActions>
              <Button size="small" disabled>Coming Soon</Button>
              <Button size="small" href="https://github.com" target="_blank">View on GitHub</Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Deployment Tools */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DeployIcon sx={{ mr: 1, fontSize: 32, color: 'success.main' }} />
                <Typography variant="h6">Deployment Tools</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Deploy applications, monitor deployments, and manage environments.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><DeployIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Deploy to staging" secondary="Push code to staging environment" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><DeployIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="View deployment logs" secondary="Monitor deployment status" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><DeployIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Rollback deployments" secondary="Revert to previous version" />
                </ListItem>
              </List>
            </CardContent>
            <CardActions>
              <Button size="small" disabled>Coming Soon</Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Database Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DatabaseIcon sx={{ mr: 1, fontSize: 32, color: 'info.main' }} />
                <Typography variant="h6">Database Management</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                View database connections, run queries, and manage migrations.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><DatabaseIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Database explorer" secondary="Browse tables and data" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TerminalIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Query console" secondary="Run SQL queries" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><DatabaseIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Migration manager" secondary="View and run migrations" />
                </ListItem>
              </List>
            </CardContent>
            <CardActions>
              <Button size="small" disabled>Coming Soon</Button>
            </CardActions>
          </Card>
        </Grid>

        {/* API Testing Console */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ApiIcon sx={{ mr: 1, fontSize: 32, color: 'warning.main' }} />
                <Typography variant="h6">API Testing Console</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Test API endpoints, view documentation, and debug requests.
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><ApiIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="API documentation" secondary="View OpenAPI/Swagger docs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><BugIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Endpoint testing" secondary="Send test requests" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TerminalIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Request history" secondary="View past API calls" />
                </ListItem>
              </List>
            </CardContent>
            <CardActions>
              <Button size="small" disabled>Coming Soon</Button>
              <Button size="small" href="/api/documentation" target="_blank">View API Docs</Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Coming Soon:</strong> These tools are currently under development. 
            In the meantime, use the provided external links to access GitHub, Swagger docs, etc.
          </Typography>
        </Alert>
      </Box>

      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          External Tools
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Quick access to commonly used external development tools
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              startIcon={<GitHubIcon />}
              href="https://github.com"
              target="_blank"
            >
              GitHub
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              startIcon={<ApiIcon />}
              href="/api/documentation"
              target="_blank"
            >
              API Docs
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              startIcon={<DatabaseIcon />}
              disabled
            >
              phpMyAdmin
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              startIcon={<TerminalIcon />}
              disabled
            >
              CI/CD Pipeline
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default DeveloperToolsPage;
